import { useEffect, useState } from "react";

export type LoopbackPermissionState = PermissionState | "unsupported" | "pending";

// The 3Dconnexion NL-Proxy lives on a loopback address (127.x), so the relevant Chrome permission is
// "loopback-network" (Chrome 147+). Older builds only expose the "local-network-access" alias.
const PERMISSION_NAMES: readonly string[] = ["loopback-network", "local-network-access"];

/**
 * Tracks Chrome's Local Network Access permission for loopback requests, kept in sync via the
 * PermissionStatus `change` event.
 *
 * Returns "pending" until the first query resolves, and "unsupported" when the Permissions API
 * doesn't recognize the name (non-Chromium, older Chrome, or insecure context) — in which case denial
 * can't be detected and the feature falls back to attempt-and-fail behavior.
 *
 * A "denied" result cannot be re-prompted programmatically; Chrome only lets the user re-grant it from
 * the address-bar site settings.
 */
export function useLoopbackPermissionState(): LoopbackPermissionState {
    const [state, setState] = useState<LoopbackPermissionState>("pending");

    useEffect(() => {
        const perms = navigator.permissions;
        if (!perms?.query) {
            setState("unsupported");
            return;
        }

        let status: PermissionStatus | null = null;
        let cancelled = false;
        const sync = () => {
            if (status) {
                setState(status.state);
            }
        };

        void (async () => {
            for (const name of PERMISSION_NAMES) {
                try {
                    const result = await perms.query({ name: name as PermissionName });
                    if (cancelled) {
                        return;
                    }
                    status = result;
                    setState(result.state);
                    result.addEventListener("change", sync);
                    return;
                } catch {
                    // Name not recognized by this browser; try the next.
                }
            }
            if (!cancelled) {
                setState("unsupported");
            }
        })();

        return () => {
            cancelled = true;
            status?.removeEventListener("change", sync);
        };
    }, []);

    return state;
}
